import React, { useState, useEffect } from 'react'
import './LinkNewScreen.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { linkScreen } from '../../../services/linkScreen';
import { getScreenHostAccount } from '../../../services/getScreenHostAccount';
import { RingLoader } from 'react-spinners';
import UserNav from '../UserNav/UserNav';
import posthog from '../../../services/posthog';
import { getUserAccessToken, getUserSession, persistUserSession } from '../../../services/userSession';
import { normalizeUserPhone } from '../../../utils/userRouting';

export default function LinkNewScreen() {
    const [screenCode, setScreenCode] = useState('');
    const [screenName, setScreenName] = useState('');
    const [loading, setLoading] = useState(false);
    const [selfServeSignup, setSelfServeSignup] = useState(false);
    const [accountUserId, setAccountUserId] = useState(null);
    const { state } = useLocation();
    const session = getUserSession();
    const phoneNumber = state?.phoneNumber || session?.phoneNumber;
    const token = getUserAccessToken();
    const userId = accountUserId ?? state?.userId ?? session?.userId ?? session?.ownerUserId ?? null;

    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        if (!phoneNumber || !token) {
            navigate('/userLogin/')
        }
    }, [navigate, phoneNumber, token]);

    useEffect(() => {
        if (!token) return;
        getScreenHostAccount(token)
            .then((res) => {
                setSelfServeSignup(Boolean(res.data?.selfServeSignup));
                if (res.data?.userId != null) {
                    setAccountUserId(res.data.userId);
                    persistUserSession({ userId: res.data.userId });
                }
            })
            .catch(() => {
                setSelfServeSignup(false);
            });
    }, [token]);

    function screenCodeOnChange(value) {
        var reg = /^\d+$/;
        if (value === '') {
            setScreenCode('');
            return;
        }
        if (reg.test(value)) {
            setScreenCode(value);
        }
    }

    function getDashboardPath() {
        return selfServeSignup ? '/user/screens' : '/user/todo';
    }

    function navigateToScreenDetails(linkedScreenId) {
        const normalizedPhone = normalizeUserPhone(phoneNumber);
        const routeState = {
            phoneNumber: normalizedPhone,
            token,
            userId,
            screenId: linkedScreenId,
        };
        persistUserSession({
            phoneNumber: normalizedPhone,
            screenId: linkedScreenId,
            ...(userId != null ? { userId } : {}),
        });
        navigate('/user/userDashboard/screenInfo', { state: routeState });
    }

    function onSubmit(e) {
        e.preventDefault();
        setLoading(true);
        setError(false);
        setErrorMessage('');

        if (screenCode.length === 0) {
            setError(true);
            setErrorMessage('Please check the screen code and try again');
            setLoading(false);
            return;
        }
        if (!phoneNumber || !token) {
            setError(true);
            setErrorMessage('Please check the screen code and try again');
            setLoading(false);
            return;
        }

        const trimmedName = screenName.trim();

        linkScreen(phoneNumber, screenCode, trimmedName || null, token).then((res) => {
            setError(false);
            setLoading(false);
            const linkedScreenId = res.data?.screenId ?? Number(screenCode);
            posthog.capture('screen linked', { screen_code: screenCode, screen_name: trimmedName || null });
            navigateToScreenDetails(linkedScreenId);
        }).catch((err) => {
            posthog.captureException(err, { step: 'screen linked', screen_code: screenCode });
            setError(true);
            setLoading(false);
            const apiError = err?.response?.data?.error;
            setErrorMessage(apiError || 'Please check the screen code and try again');
        });
    }

    return (
        <div className='linkNewScreenContainer'>
            <UserNav />

            <div className='linkNewScreenContent'>
                <h1 className='linkNewScreenTitle'>Link A New Screen</h1>

                <div className='stepsContainer'>
                    <span className='stepsTitle'>Before Linking:</span>
                    <ul className='stepsList'>
                        <li>Turn on your Screen</li>
                        <li>Complete the on-screen Wi-Fi setup</li>
                        <li>Connect to the same network as your screen</li>
                    </ul>
                </div>

                <form onSubmit={onSubmit} className='inputContainer'>
                    <input
                        className='screenCode'
                        type='number'
                        value={screenCode}
                        onChange={(e) => { screenCodeOnChange(e.target.value) }}
                        placeholder='Enter Screen Code'
                        disabled={loading}
                        aria-label="Screen code input"
                    />

                    <input
                        className='screenNameInput'
                        type='text'
                        value={screenName}
                        onChange={(e) => setScreenName(e.target.value)}
                        placeholder='Screen name (optional, e.g. Front window TV)'
                        maxLength={100}
                        disabled={loading}
                        aria-label="Screen name input"
                    />

                    {error && <span className='errorText'>{errorMessage}</span>}

                    {!loading && (
                        <button
                            type="submit"
                            className='searchButton'
                            aria-label="Search and pair screen"
                        >
                            Search & Pair
                        </button>
                    )}

                    {loading && (
                        <RingLoader
                            color={"#C52F00"}
                            loading={true}
                            size={40}
                            aria-label="Loading Spinner"
                            data-testid="loader"
                            className='searchingSpinner'
                        />
                    )}

                    <span
                        className='backButton'
                        onClick={() => navigate(getDashboardPath(), { state: { phoneNumber: normalizeUserPhone(phoneNumber), token, userId } })}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                navigate(getDashboardPath(), { state: { phoneNumber: normalizeUserPhone(phoneNumber), token, userId } });
                            }
                        }}
                    >
                        ← Back to Dashboard
                    </span>
                </form>
            </div>
        </div>
    )
}
